import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { C } from '../theme'

function toISODate(d) {
  return d.toISOString().split('T')[0]
}

// Montag der Woche, in der `d` liegt
function wochenstart(d) {
  const x = new Date(d)
  const tag = (x.getDay() + 6) % 7 // Mo=0 ... So=6
  x.setDate(x.getDate() - tag)
  x.setHours(0, 0, 0, 0)
  return x
}

function monatKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function Statistik({ isMobile }) {
  const [periode, setPeriode]   = useState('woche')
  const [abgeschlossen, setAbgeschlossen] = useState([]) // canlı: teslim edilmiş araçlar
  const [archiv, setArchiv]     = useState([])           // monats_statistik arşivi
  const [loading, setLoading]   = useState(true)

  useEffect(() => { laden() }, [])

  const laden = async () => {
    setLoading(true)
    const [{ data: fertig }, { data: arch }] = await Promise.all([
      supabase.from('fahrzeuge')
        .select('eingang, übergabe_datum, problem, fastcars')
        .eq('übergabe_ok', true),
      supabase.from('monats_statistik').select('*'),
    ])
    setAbgeschlossen(fertig || [])
    setArchiv(arch || [])
    setLoading(false)
  }

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Lädt...</div>
  )

  const heute = new Date()

  /* ── Periodengrenzen ── */
  const wocheVon = wochenstart(heute)
  const monatVon = new Date(heute.getFullYear(), heute.getMonth(), 1)
  const von = periode === 'woche' ? wocheVon : monatVon

  const imZeitraum = abgeschlossen.filter(f =>
    f.übergabe_datum && f.übergabe_datum >= toISODate(von)
  )

  const dauerListe = imZeitraum
    .filter(f => f.eingang)
    .map(f => (new Date(f.übergabe_datum) - new Date(f.eingang)) / 86400000)
  const avgDauer = dauerListe.length > 0
    ? Math.round((dauerListe.reduce((a, b) => a + b, 0) / dauerListe.length) * 10) / 10
    : 0

  const KARTEN = [
    { label: 'Abgeschlossen',        val: imZeitraum.length, icon: '✅', bg: C.successBg },
    { label: 'Ø Bearbeitungsdauer',  val: avgDauer, suffix: 'Tage', icon: '⏱️', bg: C.primarySoft },
    { label: 'Mit Problemen',        val: imZeitraum.filter(f => f.problem).length, icon: '⚠️', bg: C.dangerBg },
    { label: 'FastCars-Aufträge',    val: imZeitraum.filter(f => f.fastcars).length, icon: '🔧', bg: C.fastcarsBg },
  ]

  /* ── Wochen-Chart: letzte 8 Wochen ── */
  const wochen = []
  for (let i = 7; i >= 0; i--) {
    const start = new Date(wocheVon)
    start.setDate(start.getDate() - i * 7)
    const ende = new Date(start)
    ende.setDate(ende.getDate() + 7)
    const count = abgeschlossen.filter(f =>
      f.übergabe_datum &&
      f.übergabe_datum >= toISODate(start) &&
      f.übergabe_datum < toISODate(ende)
    ).length
    wochen.push({
      label: `${start.getDate()}.${start.getMonth() + 1}.`,
      count,
      istAktuell: i === 0,
    })
  }

  /* ── Monats-Chart: letzte 12 Monate (Archiv + aktueller Monat live) ── */
  const monate = []
  for (let i = 11; i >= 0; i--) {
    const d   = new Date(heute.getFullYear(), heute.getMonth() - i, 1)
    const key = monatKey(d)
    const archRow  = archiv.find(a => a.monat === key)
    const liveCount = abgeschlossen.filter(f => f.übergabe_datum && f.übergabe_datum.startsWith(key)).length
    monate.push({
      key,
      label: d.toLocaleDateString('de-DE', { month: 'short' }),
      count: (archRow?.abgeschlossen || 0) + liveCount,
      istAktuell: i === 0,
    })
  }

  const chartDaten = periode === 'woche' ? wochen : monate
  const maxVal = Math.max(...chartDaten.map(x => x.count), 1)

  return (
    <div style={{ padding: isMobile ? '18px 14px' : '24px 28px', maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Statistiken</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#9ca3af' }}>
            {periode === 'woche'
              ? `Diese Woche (ab ${wocheVon.toLocaleDateString('de-DE')})`
              : `Dieser Monat (${heute.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })})`}
          </p>
        </div>

        {/* Perioden-Umschalter */}
        <div style={{ display: 'flex', background: '#e2e8f0', borderRadius: 9, padding: 3 }}>
          {[['woche', 'Woche'], ['monat', 'Monat']].map(([k, l]) => (
            <button key={k} onClick={() => setPeriode(k)} style={{
              padding: '7px 22px', border: 'none', borderRadius: 7, cursor: 'pointer',
              fontSize: 13, fontWeight: 700,
              background: periode === k ? C.primary : 'transparent',
              color: periode === k ? C.onPrimary : '#64748b',
              transition: 'all 0.15s',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Stat-Karten */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12, margin: '18px 0' }}>
        {KARTEN.map(k => (
          <div key={k.label} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }}>
                {k.icon}
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
                  {k.val}
                  {k.suffix && <span style={{ fontSize: 12, fontWeight: 500, color: '#9ca3af', marginLeft: 3 }}>{k.suffix}</span>}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{k.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          {periode === 'woche' ? 'Abgeschlossene Fahrzeuge — letzte 8 Wochen' : 'Abgeschlossene Fahrzeuge — letzte 12 Monate'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: isMobile ? 4 : 8, height: 150, paddingTop: 22 }}>
          {chartDaten.map(x => (
            <div key={x.key || x.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{x.count || ''}</div>
              <div style={{
                width: '100%', borderRadius: '5px 5px 0 0',
                background: x.istAktuell ? C.primary : '#e9d391',
                height: `${Math.max(Math.round((x.count / maxVal) * 100), x.count > 0 ? 4 : 1)}px`,
                transition: 'height 0.3s',
              }} />
              <div style={{ fontSize: 10, color: x.istAktuell ? C.text : '#9ca3af', fontWeight: x.istAktuell ? 700 : 400, whiteSpace: 'nowrap' }}>{x.label}</div>
            </div>
          ))}
        </div>
        {periode === 'monat' && (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
            Vergangene Monate stammen aus dem Statistik-Archiv — Fahrzeugdaten werden am 3. jedes Monats automatisch bereinigt.
          </div>
        )}
      </div>
    </div>
  )
}
